// pos_local order (/v1/orders/*) → front-un masa-mərkəzli cloud order shape-i.
//
// Front "table.orders[0]" gözləyir; pos_local order-mərkəzlidir (orderId ilə).
// Gateway bu tərcüməni edir: masa → currentOrderId → /v1/orders/:id.

import { stripPosId, parseI18nName } from './catalogMappers';

/**
 * Sətrə seçilmiş opsiya. Ad və qiymət SNAPSHOT-dur: sifariş verildiyi andakı
 * dəyər saxlanılır, sonra admin qiyməti dəyişsə köhnə sifariş öz məbləğini
 * saxlayır.
 */
export interface PosOrderItemModifier {
  id: string;
  nameSnapshot: string;
  /** MƏNFİ ola bilər ("Sossuz" −0,50). */
  priceDeltaCentsSnapshot: number;
}

export interface PosOrderItem {
  id: string; // order item id (cloud `pivot_id` = item silmə/azaltma üçün)
  productId: string; // "prd_42"
  productNameSnapshot: string;
  unitPriceCents: number;
  quantity: number;
  isVoided: boolean;
  modifiers?: PosOrderItemModifier[];
}

/** Hesaba əlavə olunan haqq (servis haqqı və s.) — pos_local hesablayır. */
export interface PosAppliedCharge {
  id: string;
  name: string;
  type: 'percent' | 'fixed';
  /** percent → basis point (1000 = 10%); fixed → qəpik. */
  value: number;
  amountCents: number;
}

export interface PosOrder {
  id: string;
  tableId?: string | null;
  status: string; // open | kitchen | closed | void
  items: PosOrderItem[];
  totals: {
    subtotalCents: number;
    taxCents: number;
    chargeCents?: number;
    charges?: PosAppliedCharge[];
    totalCents: number;
  };
}

/**
 * pos order item → cloud `stocks[]` elementi.
 *
 * `price` OPSİYA FƏRQİNİ DƏ EHTİVA EDİR. Əvvəl yalnız `unitPriceCents`
 * qaytarılırdı; opsiyalar POS-dan seçilə bilən olandan sonra bu adisyonda
 * yanlış sətir qiyməti göstərərdi:
 *
 *   ekranda sətir  ₼16,80   ← baza
 *   hesab cəmi     ₼24,10   ← pos_local fərqi əlavə edib
 *
 * Cəm doğru, sətir yanlış — ofisiant fərqi izah edə bilməzdi.
 * `base_price` ayrıca verilir ki, lazım olsa bazanı da göstərmək mümkün olsun.
 */
export function posItemToStock(it: PosOrderItem): Record<string, unknown> {
  const modifiers = it.modifiers ?? [];
  const deltaCents = modifiers.reduce(
    (sum, m) => sum + (m.priceDeltaCentsSnapshot ?? 0),
    0,
  );
  return {
    id: stripPosId(it.productId),
    name: parseI18nName(it.productNameSnapshot),
    quantity: it.quantity,
    price: (it.unitPriceCents + deltaCents) / 100,
    base_price: it.unitPriceCents / 100,
    // Cloud shape-də belə sahə yoxdur; POS paneli varsa göstərir, yoxsa atlayır.
    modifiers: modifiers.map((m) => ({
      id: m.id,
      name: parseI18nName(m.nameSnapshot),
      price_delta: (m.priceDeltaCentsSnapshot ?? 0) / 100,
    })),
    pivot_id: it.id,
  };
}

/**
 * GET /tables/:id/order cavabı:
 *   { table: { name, orders: [{ order_id, total_price, total_prepayment, stocks[], sets[] }] } }
 * order yoxdursa orders: [].
 */
export function posOrderToCloudTableOrder(
  tableName: string,
  order: PosOrder | null,
): Record<string, unknown> {
  if (!order || order.status === 'closed' || order.status === 'void') {
    return { table: { name: tableName, orders: [] } };
  }
  const stocks = (order.items ?? []).filter((i) => !i.isVoided).map(posItemToStock);
  const totalCents = order.totals?.totalCents ?? 0;
  const subtotalCents = order.totals?.subtotalCents ?? totalCents;
  // Hesab haqları (servis haqqı və s.) — cloud shape-də belə sahə yoxdur, əlavə
  // olaraq ötürürük ki, POS paneli hesabı sətir-sətir göstərə bilsin. Cloud
  // rejimində bu sahə sadəcə mövcud olmur və UI onu atlayır.
  const charges = (order.totals?.charges ?? []).map((c) => ({
    id: c.id,
    name: c.name,
    type: c.type,
    amount: c.amountCents / 100,
  }));
  return {
    table: {
      name: tableName,
      orders: [
        {
          id: order.id,
          order_id: order.id,
          total_price: totalCents / 100,
          subtotal_price: subtotalCents / 100,
          charges,
          total_prepayment: 0,
          stocks,
          sets: [],
        },
      ],
    },
  };
}
